import { useEffect } from 'react'
import { Text } from 'blaze-ui/Text/Text'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Trash2 } from 'blaze-ui/icons'
import { ModalTrigger } from 'almanac-ui/Modal/ModalTrigger'
import { isSourceAnalysisPending, usePollSource } from 'hooks/queries/workspaces/usePollSource'
import { CampaignSource } from './Types'
import { AnalysisSummaryModal } from './AnalysisSummaryModal'
import { SourceAnalysisBody } from './SourceAnalysisBody'
import { SourceIcon, getSourceLabel } from './AddContext'
import Styles from './AddContext.module.scss'
import { Button } from 'blaze-ui/Button/Button'
import { FileEye2 } from 'blaze-ui/icons'

export interface SourceCardProps {
  campaignSource: CampaignSource
  isUploading: boolean
  onRemove: (id: number | string) => void
  onAnalysisSettled?: (sourceId: number | string) => void
}

export const SourceCard = ({ campaignSource, isUploading, onRemove, onAnalysisSettled }: SourceCardProps) => {
  const { name, type } = getSourceLabel(campaignSource.source)
  const isSourceMaterialRole = campaignSource.roles.includes('source_material')

  const { data: polledSource } = usePollSource(campaignSource.source.id, campaignSource.source.type, {
    enabled: isSourceMaterialRole,
  })

  const isAnalysisPending = isSourceAnalysisPending(polledSource)

  const analysis =
    polledSource?.source_materials?.find((material) => material.source_material_analysis != null)
      ?.source_material_analysis ?? null

  // A source stops blocking the flow once polling settles — either it has an
  // analysis, or the file couldn't be read and won't be used as context.
  useEffect(() => {
    if (!isAnalysisPending) onAnalysisSettled?.(campaignSource.source.id)
  }, [isAnalysisPending]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={Styles.sourceCard}>
      <div className={Styles.sourceCardTop}>
        <div className={Styles.sourceIcon}>
          <SourceIcon source={campaignSource.source} />
        </div>
        <div className={Styles.sourceInfo}>
          <Text variant="secondary" color="var(--dark-90)" lineClamp={1}>
            {name}
          </Text>
          <Text variant="metadata" color="var(--dark-60)" lineClamp={1}>
            {type} used as a {campaignSource.roles.includes('source_material') ? 'source material' : 'visual asset'}
          </Text>
        </div>
        {!['Image', 'Video'].includes(type) && (
          <ModalTrigger
            modal={AnalysisSummaryModal}
            name={name}
            analysis={analysis ?? { id: 0, summary: '', analysis: '' }}
            isDisabled={!analysis}
          >
            <Button variant="tertiary" frontIcon={FileEye2} size="sm" isDisabled={!analysis}>
              View Summary
            </Button>
          </ModalTrigger>
        )}
        <IconButton
          icon={Trash2}
          variant="ghost"
          size="sm"
          title="Remove source"
          isDisabled={isUploading}
          onPress={() => onRemove(campaignSource.source.id)}
        />
      </div>
      {isSourceMaterialRole && (
        <div className={Styles.analysisSection}>
          <SourceAnalysisBody isPending={isAnalysisPending} analysis={analysis} />
        </div>
      )}
    </div>
  )
}
